/**
 * Cognara AI draft studio — upload the papers, get a reviewed draft back.
 *
 * Nothing reaches a register until a human accepts it, so the analysis comes
 * first and the editable draft second. The counterparty's case is given the
 * same prominence as ours by design: a drafting tool that only flatters the
 * instructing party is worse than no tool.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import ReactMarkdown from 'react-markdown';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Progress } from '@/components/ui/progress';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Sparkles, Loader2, FileText, Scale, ShieldAlert, CheckCircle2, XCircle,
  Quote, TriangleAlert, Gavel, Upload, Plus, Download, MessageSquare, Send,
  RefreshCw, GitCompare, History, Clock, FolderOpen, FileSignature, LayoutList,
  Building2, HardHat,
} from 'lucide-react';

const KIND_LABEL = {
  claim: 'Claim', ld: 'LD tracker', variation: 'Variation',
  dispute: 'Dispute', correspondence: 'Correspondence',
};

const STRENGTH_TONE = {
  STRONG: 'bg-emerald-500/15 text-emerald-500 border-emerald-500/30',
  ARGUABLE: 'bg-amber-500/15 text-amber-500 border-amber-500/30',
  WEAK: 'bg-red-500/15 text-red-500 border-red-500/30',
};

const TIMEBAR_TONE = {
  COMPLIANT: 'text-emerald-500', AT_RISK: 'text-amber-500',
  BREACHED: 'text-red-500', UNKNOWN: 'text-slate-400',
};

const money = (v, ccy) =>
  v == null ? '—' : `${ccy || ''} ${Number(v).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`.trim();

// Older sessions stored the raw engine error before it was being mapped, and
// older builds told people to run on fewer documents — a limitation that no
// longer exists, so it is never shown again.
const humanError = (e) => {
  const s = String(e || '');
  if (s.trim().startsWith('Error code:') || /fewer documents/i.test(s)) {
    return 'Cognara could not complete this analysis. Run it again — the pages already read are cached, so it picks up from there.';
  }
  return s;
};

// A locator is only useful if the reader can open it: pull the page or the row
// out of "Sch II cl. 7(a), p. 11" or "sheet 'Pending jobs', row 26".
const citationSpot = (locator) => {
  const s = String(locator || '');
  const row = s.match(/\browe?\s*(\d+)/i) || s.match(/\bR(\d+)\b/);
  if (row) return { row: Number(row[1]) };
  const page = s.match(/\bp(?:age|p|\.)?\s*\.?\s*(\d+)/i);
  if (page) return { page: Number(page[1]) };
  return null;
};

export const CognaraDraftStudio = ({ open, onOpenChange, contractId, kind, documents = [], onAccepted }) => {
  const [mode, setMode] = useState('INDEPENDENT_REVIEW');
  const [modes, setModes] = useState([]);
  const [instructions, setInstructions] = useState('');
  const [picked, setPicked] = useState([]);
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState(null);
  const [draft, setDraft] = useState(null);
  const [edits, setEdits] = useState({});
  const [accepting, setAccepting] = useState(false);
  const [docs, setDocs] = useState(documents);
  const [uploading, setUploading] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [question, setQuestion] = useState('');
  const [asking, setAsking] = useState(false);
  const [direction, setDirection] = useState('');
  const [revising, setRevising] = useState(false);
  const [exporting, setExporting] = useState('');
  const [label, setLabel] = useState('');
  const [tree, setTree] = useState(null);
  const [letters, setLetters] = useState([]);
  const [lettering, setLettering] = useState('');
  // Readings already held for these documents, and what to do with them.
  const [readings, setReadings] = useState({});
  const [rescan, setRescan] = useState('none');
  const [stopping, setStopping] = useState(false);
  // The paper behind a citation: the cited page image, or the cited rows.
  const [source, setSource] = useState(null);
  const readCount = React.useMemo(
    () => picked.filter(p => readings[p]?.read).length, [picked, readings]);

  // Documents are picked folder by folder, the way they are filed on the contract.
  const folderGroups = React.useMemo(() => {
    const known = new Set();
    const groups = (tree?.folders || []).map(f => {
      (f.documents || []).forEach(d => known.add(d.storage_path || d.file_url));
      return { folder_id: f.folder_id, name: f.name, documents: f.documents || [] };
    }).filter(g => g.documents.length);
    const loose = docs.filter(d => !known.has(d.storage_path || d.file_url));
    if (loose.length) groups.push({ folder_id: 'unfiled', name: 'Unfiled', documents: loose });
    return groups.length ? groups : [{ folder_id: 'all', name: 'All documents', documents: docs }];
  }, [tree, docs]);
  const [compareWith, setCompareWith] = useState('');
  const [viewRevision, setViewRevision] = useState(null);
  const pollRef = useRef(0);   // bumped on close so a stale poller stops
  const runningIdRef = useRef(null);   // the session a Stop would apply to

  const stop = async () => {
    const id = runningIdRef.current;
    if (!id) return;
    setStopping(true);
    try {
      await api.post(`/contracts-ai/drafts/${id}/stop`);
      toast.info('Stopping — the pages already read are kept');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not stop the analysis');
    } finally { setStopping(false); }
  };

  const openSource = useCallback(async (c) => {
    const spot = citationSpot(c?.locator);
    if (!draft?.id || !spot) return;
    setSource({ loading: true, citation: c, spot });
    try {
      const { data } = await api.get(`/contracts-ai/drafts/${draft.id}/source`, {
        params: { document: c.document, ...spot },
      });
      setSource({ loading: false, citation: c, spot, data });
    } catch (e) {
      setSource({ loading: false, citation: c, spot,
                  error: e?.response?.data?.detail || 'That source could not be opened' });
    }
  }, [draft?.id]);

  const loadSessions = useCallback(async () => {
    try {
      const { data } = await api.get(
        `/contracts-ai/contracts/${contractId}/drafts`, { params: { kind, limit: 50 } });
      setSessions(data.drafts || []);
      return data.drafts || [];
    } catch { return []; }
  }, [contractId, kind]);

  useEffect(() => {
    if (!open) return;
    setDraft(null); setEdits({}); setInstructions(''); setCompareWith('');
    setQuestion(''); setDirection(''); setViewRevision(null); setLetters([]);
    setDocs(documents);
    setPicked(documents.map(d => d.storage_path || d.file_url).filter(Boolean));
    api.get('/contracts-ai/modes').then(({ data }) => setModes(data.modes || [])).catch(() => {});
    api.get(`/contracts/contracts/${contractId}/folders`)
      .then(({ data }) => setTree(data)).catch(() => setTree(null));
    setRescan('none');
    api.get(`/contracts-ai/contracts/${contractId}/readings`)
      .then(({ data }) => setReadings(Object.fromEntries(
        (data.readings || []).map(r => [r.path, r]))))
      .catch(() => setReadings({}));
    // A run costs money and takes minutes — never lose one because the dialog
    // was closed. Pick up whatever is already running or awaiting review.
    loadSessions().then((rows) => {
      const live = rows.find(d => d.status === 'RUNNING' || d.status === 'PENDING');
      if (!live) return;
      if (live.status === 'PENDING') {
        setDraft(live); setEdits(live.draft || {});
        toast.info('Picked up the session you left open');
      } else {
        toast.info('A review is still running — reattaching to it');
        resume(live.id);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, documents, contractId, kind]);

  const upload = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setUploading(true);
    try {
      const fd = new FormData();
      files.forEach(f => fd.append('files', f));
      await api.post(`/contracts/contracts/${contractId}/documents`, fd,
        { headers: { 'Content-Type': 'multipart/form-data' } });
      const { data } = await api.get(`/contracts/contracts/${contractId}`);
      const fresh = data.documents || [];
      setDocs(fresh);
      // Tick the new arrivals: they are why the user opened the picker.
      const names = new Set(files.map(f => f.name));
      setPicked(p => [...new Set([...p, ...fresh
        .filter(d => names.has(d.file_name))
        .map(d => d.storage_path || d.file_url)
        .filter(Boolean)])]);
      toast.success(`${files.length} document(s) uploaded`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Upload failed');
    } finally { setUploading(false); }
  };

  const resume = async (id) => {
    const token = ++pollRef.current;
    setRunning(true);
    runningIdRef.current = id;
    try {
      // A 1,000-page set can read for a long time; the poller only stops when
      // the dialog closes or the session leaves RUNNING.
      let misses = 0;
      for (;;) {
        await new Promise(r => setTimeout(r, 3000));
        if (token !== pollRef.current) return;      // the dialog was closed
        let d;
        try {
          ({ data: d } = await api.get(`/contracts-ai/drafts/${id}`));
          misses = 0;
        } catch (err) {
          // A blip on a run that reads for an hour must not abandon the session.
          if (++misses > 40) throw err;
          continue;
        }
        setProgress(d.progress || null);
        if (d.status === 'FAILED') throw new Error(d.error || 'The analysis failed');
        if (d.status === 'CANCELLED') {
          setProgress(null);
          toast.info(d.error || 'Analysis stopped. What was read is kept.');
          return;
        }
        if (d.status !== 'RUNNING') {
          setDraft(d); setEdits(d.draft || {});
          setProgress(null);
          // A revision that could not be rebuilt comes back as PENDING with the
          // old analysis and an error — that is a failure, not a finished review.
          if (d.error) toast.error(d.error);
          else toast.success('The review is complete');
          return;
        }
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || 'The review could not be completed');
    } finally {
      // Only the ACTIVE poller may clear the running state. A superseded poller
      // used to wipe the live one's progress area on its way out, so the bar
      // vanished while the analysis was still running.
      if (token === pollRef.current) { setRunning(false); setProgress(null); }
      loadSessions?.();
    }
  };

  const run = useCallback(async () => {
    if (!picked.length) { toast.error('Select at least one document for Cognara to read.'); return; }
    setRunning(true);
    try {
      const { data } = await api.post(`/contracts-ai/contracts/${contractId}/drafts`, {
        kind, mode, instructions, documentPaths: picked, label: label.trim(),
        rescan: rescan === 'selected' ? picked.filter(p => readings[p]?.read) : rescan,
      });
      await loadSessions();
      // A thorough read runs past any HTTP timeout, so the draft is filled in
      // server-side and polled here.
      await resume(data.id);
      await loadSessions();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message
        || 'Cognara AI could not complete the analysis');
      setRunning(false);
    }
  }, [contractId, kind, mode, instructions, picked, label, loadSessions, rescan, readings]);

  const accept = async () => {
    setAccepting(true);
    try {
      const { data } = await api.post(`/contracts-ai/drafts/${draft.id}/accept`, { draft: edits });
      toast.success(`${KIND_LABEL[kind]} created from the accepted draft`);
      onAccepted?.(data);
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not accept the draft');
    } finally { setAccepting(false); }
  };

  const reject = async () => {
    try {
      await api.post(`/contracts-ai/drafts/${draft.id}/reject`, { reason: 'Rejected on review' });
      toast.info('Draft rejected — nothing was written to the register');
      setDraft(null); loadSessions();
    } catch { toast.error('Could not reject the draft'); }
  };

  // ── sessions ───────────────────────────────────────────────────────
  const openSession = async (id) => {
    try {
      // Stop any poller still attached to the session being left behind, and
      // use the same token so a slow GET cannot overwrite a newer selection.
      const token = ++pollRef.current;
      setRunning(false);
      // Drop the previous session's percentage, or the area briefly shows the
      // figure from the run you just left.
      setProgress(null);
      const { data } = await api.get(`/contracts-ai/drafts/${id}`);
      if (token !== pollRef.current) return;
      setViewRevision(null); setQuestion(''); setDirection(''); setCompareWith('');
      if (data.status === 'RUNNING') { setDraft(null); setProgress(data.progress || null); resume(id); return; }
      setDraft(data); setEdits(data.draft || {});
      setLetters(data.letters || []);
      // A letter still being written keeps its poller when the session is reopened.
      (data.letters || []).filter(l => l.status === 'RUNNING')
        .forEach(l => pollLetter(data.id, l.letter_id));
      if (data.error) toast.error(humanError(data.error));
    } catch { toast.error('Could not open that session'); }
  };

  const newSession = () => {
    pollRef.current += 1; setRunning(false);
    setDraft(null); setEdits({}); setInstructions(''); setViewRevision(null);
    setLabel(''); setCompareWith(''); setQuestion(''); setDirection('');
    setMode('INDEPENDENT_REVIEW'); setLetters([]);
    setPicked(docs.map(d => d.storage_path || d.file_url).filter(Boolean));
  };

  const ask = async () => {
    const q = question.trim();
    if (!q) return;
    setAsking(true);
    setQuestion('');
    // The answer is produced in the background — a synchronous reply was cut off
    // by the gateway and the chat never responded. The turn appears at once and
    // fills in when Cognara is done.
    setDraft(d => ({ ...d, conversation: [...(d.conversation || []),
                                         { question: q, answer: '', status: 'RUNNING' }] }));
    try {
      const { data } = await api.post(`/contracts-ai/drafts/${draft.id}/ask`, { question: q });
      const idx = data.index;
      for (;;) {
        await new Promise(r => setTimeout(r, 3000));
        let turn;
        try {
          const { data: s } = await api.get(`/contracts-ai/drafts/${draft.id}`);
          turn = (s.conversation || [])[idx];
          setDraft(d => ({ ...d, conversation: s.conversation || [] }));
        } catch { continue; }
        if (!turn || turn.status === 'RUNNING') continue;
        if (turn.status === 'FAILED') {
          toast.error(turn.error || 'Cognara could not answer that');
          setQuestion(q);
        }
        break;
      }
    } catch (e) {
      setDraft(d => ({ ...d, conversation: (d.conversation || []).slice(0, -1) }));
      setQuestion(q);
      toast.error(e?.response?.data?.detail || 'Cognara could not answer that');
    } finally { setAsking(false); }
  };

  const revise = async () => {
    const d = direction.trim();
    if (!d) { toast.error('Say what Cognara should change.'); return; }
    setRevising(true);
    try {
      await api.post(`/contracts-ai/drafts/${draft.id}/revise`, { direction: d });
      setDirection('');
      toast.info('Cognara is rebuilding the analysis on your direction');
      const id = draft.id;
      setDraft(null);
      await resume(id);
      await loadSessions();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not start the revision');
    } finally { setRevising(false); }
  };

  const download = async (url, params, fallbackName, tag) => {
    setExporting(tag);
    try {
      const res = await api.get(url, { params, responseType: 'blob' });
      const disp = res.headers?.['content-disposition'] || '';
      const match = /filename="?([^"]+)"?/.exec(disp);
      const href = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = href; a.download = match ? match[1] : fallbackName;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(href), 4000);
      toast.success('Report downloaded');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'The report could not be generated');
    } finally { setExporting(''); }
  };

  const exportReport = (format) => download(
    `/contracts-ai/drafts/${draft.id}/export`,
    { format, ...(viewRevision ? { revision: viewRevision } : {}) },
    `analysis.${format}`, format);

  const exportOnePager = (format) => download(
    `/contracts-ai/drafts/${draft.id}/export`,
    { format, layout: 'onepager', ...(viewRevision ? { revision: viewRevision } : {}) },
    `board-summary.${format}`, `one-${format}`);

  // The formal six-section LD assessment. Sessions analysed before it existed
  // have it composed on demand, which takes a pass, so the studio waits for it.
  const exportAssessment = async (format) => {
    if (!draft?.id) return;
    if (!draft.ld_assessment) {
      setExporting(`ld-${format}`);
      try {
        await api.post(`/contracts-ai/drafts/${draft.id}/assessment`);
        toast.info('Composing the liquidated damages assessment — this takes a minute');
        for (let i = 0; i < 60; i += 1) {
          await new Promise(r => setTimeout(r, 6000));
          const { data: fresh } = await api.get(`/contracts-ai/drafts/${draft.id}`);
          if (fresh.ld_assessment) {
            setDraft(d => (d && d.id === fresh.id ? { ...d, ld_assessment: fresh.ld_assessment } : d));
            break;
          }
          if (fresh.assessment_status === 'FAILED') {
            toast.error(fresh.assessment_error || 'The assessment could not be composed');
            setExporting(null);
            return;
          }
        }
      } catch (e) {
        toast.error(e?.response?.data?.detail || 'The assessment could not be composed');
        setExporting(null);
        return;
      }
      setExporting(null);
    }
    download(`/contracts-ai/drafts/${draft.id}/export`,
      { format, layout: 'assessment', ...(viewRevision ? { revision: viewRevision } : {}) },
      `ld-assessment.${format}`, `ld-${format}`);
  };

  const pollLetter = useCallback(async (draftId, letterId) => {
    for (let i = 0; i < 120; i += 1) {
      await new Promise(r => setTimeout(r, 6000));
      const { data: fresh } = await api.get(`/contracts-ai/drafts/${draftId}`);
      const row = (fresh.letters || []).find(l => l.letter_id === letterId);
      if (!row || row.status === 'RUNNING') continue;
      setDraft(d => (d && d.id === draftId ? { ...d, letters: fresh.letters || [] } : d));
      setLetters(fresh.letters || []);
      if (row.status === 'FAILED') toast.error(row.error || 'The letter could not be drafted');
      else toast.success('Letter drafted — review it, then export');
      return;
    }
    toast.error('The letter is taking longer than expected — reopen the session shortly');
  }, []);

  const draftLetter = async (decision) => {
    setLettering(decision);
    try {
      const { data } = await api.post(`/contracts-ai/drafts/${draft.id}/letters`, { decision });
      setLetters(l => [...l, data]);
      setDraft(d => ({ ...d, letters: [...(d.letters || []), data] }));
      toast.info('Cognara is drafting the letter — this takes a few minutes');
      // Drafting runs server-side, so the session is polled until it lands.
      await pollLetter(draft.id, data.letter_id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Cognara could not draft that letter');
    } finally { setLettering(''); }
  };

  const exportLetter = (letterId, format) => download(
    `/contracts-ai/drafts/${draft.id}/letters/${letterId}/export`, { format },
    `letter.${format}`, `ltr-${letterId}-${format}`);

  const exportComparison = (format) => download(
    '/contracts-ai/drafts-comparison/export',
    { a: draft.id, b: compareWith, format },
    `comparison.${format}`, `cmp-${format}`);

  const activeRevision = viewRevision
    ? (draft?.revisions || []).find(r => r.revision === viewRevision) : null;
  const a = activeRevision?.analysis || draft?.analysis || {};
  const thread = activeRevision?.conversation || draft?.conversation || [];
  const hasAnalysis = !!(draft?.analysis && Object.keys(draft.analysis).length);
  const field = (k, label, type = 'text') => (
    <div className="space-y-1" key={k}>
      <Label className="text-[10px] uppercase tracking-wider text-slate-500">{label}</Label>
      {type === 'long' ? (
        <Textarea value={edits[k] ?? ''} rows={4}
                  onChange={e => setEdits(p => ({ ...p, [k]: e.target.value }))}
                  className="text-xs" data-testid={`cognara-field-${k}`} />
      ) : (
        <Input value={edits[k] ?? ''} type={type === 'number' ? 'number' : 'text'}
               onChange={e => setEdits(p => ({
                 ...p, [k]: type === 'number' ? Number(e.target.value) : e.target.value }))}
               className="h-8 text-xs" data-testid={`cognara-field-${k}`} />
      )}
    </div>
  );

  const FIELDS = {
    claim: [['title', 'Title'], ['claim_type', 'Claim type'], ['event_date', 'Event date'],
            ['requested_days', 'Days claimed', 'number'], ['requested_amount', 'Amount claimed', 'number'],
            ['description', 'Description', 'long'], ['narrative', 'Submission narrative', 'long']],
    ld: [['milestone_name', 'Milestone'], ['ld_type', 'LD type'],
         ['contractual_date', 'Contractual date'], ['forecast_date', 'Forecast date'],
         ['daily_rate', 'Daily rate', 'number'], ['cap_percent', 'Cap %', 'number'],
         ['cap_amount', 'Cap amount', 'number'], ['grace_period_days', 'Grace days', 'number'],
         ['excusable_delay_days', 'Excusable days', 'number'], ['narrative', 'Assessment', 'long']],
    variation: [['title', 'Title'], ['instruction_ref', 'Instruction ref'],
                ['instructed_by', 'Instructed by'], ['valuation_basis', 'Valuation basis'],
                ['estimated_cost', 'Estimated cost', 'number'],
                ['time_impact_days', 'Time impact (days)', 'number'],
                ['description', 'Description', 'long'], ['narrative', 'Justification', 'long']],
    dispute: [['title', 'Title'], ['amount_in_dispute', 'Amount in dispute', 'number'],
              ['resolution_route', 'Resolution route'], ['next_step', 'Next step'],
              ['our_position', 'Our position', 'long'], ['their_position', 'Their position', 'long'],
              ['description', 'Background', 'long']],
    correspondence: [['subject', 'Subject'], ['letter_type', 'Letter type'],
                     ['recipient', 'Recipient'], ['clause_ref', 'Served under clause'],
                     ['response_due_days', 'Response due (days)', 'number'],
                     ['body_html', 'Letter body', 'long']],
  };

  return (
    <>
    <Dialog open={open} onOpenChange={(o) => {
      if (accepting) return;
      // Closing mid-run is fine — the analysis finishes server-side and the
      // studio reattaches to it next time it is opened.
      if (!o) { pollRef.current += 1; setRunning(false); }
      onOpenChange(o);
    }}>
      <DialogContent className="max-w-6xl h-[90vh] overflow-hidden flex flex-col"
                     data-testid="cognara-draft-studio">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-violet-500" />
            Cognara AI — draft {kind === 'ld' ? 'an LD tracker' : `a ${KIND_LABEL[kind]?.toLowerCase()}`} from documents
          </DialogTitle>
          <DialogDescription>
            Cognara reads every selected document together and cites what it relies on.
            Nothing is written to the register until you accept.
          </DialogDescription>
        </DialogHeader>

        <div className="flex gap-4 overflow-hidden flex-1">
          <SessionRail sessions={sessions} activeId={draft?.id} onOpen={openSession}
                       onNew={newSession} />
          <div className="flex-1 overflow-hidden flex flex-col">
        {!draft ? (
          <div className="space-y-4 overflow-y-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-[10px] uppercase tracking-wider text-slate-500">
                  Review stance
                </Label>
                <Select value={mode} onValueChange={setMode} disabled={running}>
                  <SelectTrigger className="h-8 text-xs" data-testid="cognara-mode">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {(modes.length ? modes : [{ value: mode, label: mode }]).map(m => (
                      <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-[10px] text-slate-500">
                  {modes.find(m => m.value === mode)?.hint}
                </p>
              </div>
              <div className="space-y-1">
                <Label className="text-[10px] uppercase tracking-wider text-slate-500">
                  Session name (optional)
                </Label>
                <Input value={label} onChange={e => setLabel(e.target.value)} disabled={running}
                       placeholder="e.g. Lot 3 PRS — owner's assessment"
                       className="h-8 text-xs" data-testid="cognara-session-label" />
              </div>
              <div className="space-y-1">
                <Label className="text-[10px] uppercase tracking-wider text-slate-500">
                  Anything specific to focus on? (optional)
                </Label>
                <Textarea value={instructions} rows={2} disabled={running}
                          onChange={e => setInstructions(e.target.value)}
                          placeholder="e.g. concentrate on the late access to Area 300 and the notice served on 14 March"
                          className="text-xs" data-testid="cognara-instructions" />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-1.5">
                <Label className="text-[10px] uppercase tracking-wider text-slate-500">
                  Documents to read ({picked.length} of {docs.length})
                </Label>
                <button className="text-[10px] text-sky-500"
                        onClick={() => setPicked(picked.length === docs.length ? []
                          : docs.map(d => d.storage_path || d.file_url).filter(Boolean))}
                        data-testid="cognara-toggle-all-docs">
                  {picked.length === docs.length ? 'Clear all' : 'Select all'}
                </button>
                <label className={`text-[10px] inline-flex items-center gap-1 cursor-pointer
                  ${uploading ? 'text-slate-500' : 'text-violet-500'}`}
                       data-testid="cognara-upload-label">
                  {uploading ? <Loader2 className="w-3 h-3 animate-spin" />
                             : <Upload className="w-3 h-3" />}
                  {uploading ? 'Uploading…' : 'Add documents'}
                  <input type="file" multiple hidden disabled={uploading || running}
                         accept=".pdf,.doc,.docx,.rtf,.odt,.txt,.md,.csv,.xls,.xlsx,.xlsm,.ppt,.pptx,.png,.jpg,.jpeg,.webp,.heic,.heif,.tif,.tiff,image/*"
                         onChange={e => upload(e.target.files)}
                         data-testid="cognara-upload-input" />
                </label>
              </div>
              {!docs.length ? (
                <p className="text-xs text-slate-500 border border-dashed rounded-lg p-4 text-center"
                   data-testid="cognara-no-docs">
                  No documents attached yet. Use <strong>Add documents</strong> above to bring in
                  PDFs, Word, Excel, PowerPoint, text or images — Cognara needs the papers first.
                </p>
              ) : (
                <ScrollArea className="h-52 border rounded-lg">
                  <div className="p-2 space-y-2">
                    {folderGroups.map((g) => {
                      const keys = g.documents.map(d => d.storage_path || d.file_url).filter(Boolean);
                      const all = keys.length > 0 && keys.every(k => picked.includes(k));
                      return (
                        <div key={g.folder_id} data-testid={`cognara-folder-${g.folder_id}`}>
                          <div className="flex items-center gap-2 px-1 py-0.5 bg-muted/60 rounded">
                            <Checkbox checked={all} disabled={running || !keys.length}
                                      onCheckedChange={(v) => setPicked(p => v
                                        ? Array.from(new Set([...p, ...keys]))
                                        : p.filter(x => !keys.includes(x)))}
                                      data-testid={`cognara-folder-check-${g.folder_id}`} />
                            <FolderOpen className="w-3 h-3 text-orange-500 shrink-0" />
                            <span className="text-[11px] font-medium truncate">{g.name}</span>
                            <span className="ml-auto text-[10px] text-slate-500">
                              {keys.filter(k => picked.includes(k)).length}/{g.documents.length}
                            </span>
                          </div>
                          {g.documents.map((d, i) => {
                            const key = d.storage_path || d.file_url;
                            return (
                              <label key={key || i}
                                     className="flex items-center gap-2 text-xs pl-6 pr-2 py-1 rounded hover:bg-muted/50 cursor-pointer">
                                <Checkbox checked={picked.includes(key)} disabled={running}
                                          onCheckedChange={(v) => setPicked(p => v
                                            ? [...p, key] : p.filter(x => x !== key))}
                                          data-testid={`cognara-doc-${docs.findIndex(x => (x.storage_path || x.file_url) === key)}`} />
                                <FileText className="w-3 h-3 text-slate-400 shrink-0" />
                                <span className="truncate">{d.file_name || key}</span>
                                <span className="ml-auto text-[10px] text-slate-500">
                                  {d.file_size ? `${(d.file_size / 1024).toFixed(0)} KB` : ''}
                                </span>
                              </label>
                            );
                          })}
                        </div>
                      );
                    })}
                  </div>
                </ScrollArea>
              )}
            </div>

            {/* A document is read from its page images once. The reviewer decides
                when that reading is thrown away and paid for again. */}
            {readCount > 0 && (
              <div className="rounded-lg border border-border/60 px-3 py-2"
                   data-testid="cognara-rescan-control">
                <p className="text-[11px] text-slate-400 mb-1.5">
                  {readCount} of the selected document(s) have already been read page by
                  page. Reading them again costs time and credit.
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {[['none', 'Use what has already been read'],
                    ['all', 'Read every document again']].map(([v, lbl]) => (
                    <button key={v} type="button" onClick={() => setRescan(v)}
                            data-testid={`cognara-rescan-${v}`}
                            className={`px-2 py-1 rounded-full border text-[11px] transition-colors ${
                              rescan === v
                                ? 'border-violet-500 text-violet-300 bg-violet-500/10'
                                : 'border-border/60 text-slate-400 hover:text-slate-200'}`}>
                      {lbl}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {running && (
              <div className="space-y-1.5" data-testid="cognara-running">
                <Progress value={progress?.percent || 2} data-testid="cognara-progress-bar" />
                <div className="flex items-center justify-between gap-3 text-[11px]">
                  <span className="text-slate-400 truncate" data-testid="cognara-progress-step">
                    {progress?.step || 'Starting the analysis'}
                    {progress?.docsTotal
                      ? ` · ${progress.docsDone || 0} of ${progress.docsTotal} document(s)`
                      : ''}
                  </span>
                  <span className="tabular-nums text-slate-300 shrink-0"
                        data-testid="cognara-progress-pct">
                    {progress?.percent ?? 2}%
                  </span>
                </div>
                <p className="text-[11px] text-slate-500">
                  Scanned documents are read page by page, so a long set takes several
                  minutes — you can leave this open; it keeps working in the background.
                </p>
                <div className="flex justify-end">
                  <Button size="sm" variant="outline" onClick={stop} disabled={stopping}
                          className="h-7 text-[11px] text-rose-300 border-rose-500/40
                                     hover:bg-rose-500/10"
                          data-testid="cognara-stop">
                    {stopping ? <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                              : <XCircle className="w-3 h-3 mr-1" />}
                    Stop the analysis
                  </Button>
                </div>
              </div>
            )}

            <div className="flex justify-end">
              <Button onClick={run} disabled={running || !picked.length}
                      className="bg-gradient-to-r from-violet-600 to-indigo-600"
                      data-testid="cognara-run">
                {running ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                         : <Sparkles className="w-4 h-4 mr-2" />}
                Analyse & draft
              </Button>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-4 overflow-hidden flex-1">
            {/* Analysis */}
            <div className="lg:col-span-3 overflow-y-auto pr-1 space-y-3">
              <div className="flex flex-wrap items-center gap-2 rounded-lg border border-orange-500/30 bg-orange-500/5 p-2"
                   data-testid="cognara-session-toolbar">
                <span className="text-[10px] uppercase tracking-wider text-slate-500">
                  {draft.label || 'This session'}
                </span>
                {(draft.revisions?.length > 0) && (
                  <Select value={String(viewRevision || draft.revision || 1)}
                          onValueChange={(v) => setViewRevision(
                            Number(v) === (draft.revision || 1) ? null : Number(v))}>
                    <SelectTrigger className="h-7 w-[185px] text-[11px]"
                                   data-testid="cognara-revision-select">
                      <History className="w-3 h-3 mr-1" /><SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[...(draft.revisions || []).map(r => r.revision), draft.revision || 1]
                        .sort((x, y) => x - y).map(r => (
                        <SelectItem key={r} value={String(r)}>
                          Revision {r}{r === (draft.revision || 1) ? ' (current)' : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                <div className="ml-auto flex flex-wrap items-center justify-end gap-1.5">
                  {hasAnalysis && sessions.filter(s => s.id !== draft.id && s.analysis?.headline).length > 0 && (
                    <Select value={compareWith} onValueChange={setCompareWith}>
                      <SelectTrigger className="h-7 w-[190px] text-[11px]"
                                     data-testid="cognara-compare-select">
                        <GitCompare className="w-3 h-3 mr-1" />
                        <SelectValue placeholder="Compare with…" />
                      </SelectTrigger>
                      <SelectContent>
                        {sessions.filter(s => s.id !== draft.id && s.analysis?.headline).map(s => (
                          <SelectItem key={s.id} value={s.id}>
                            {s.label || s.mode?.replace(/_/g, ' ')} · rev {s.revision || 1}
                            {s.created_at ? ` · ${new Date(s.created_at).toLocaleDateString('en-GB',
                              { day: '2-digit', month: 'short' })}` : ''}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                  {compareWith ? (
                    <>
                      <Button size="sm" variant="outline" className="h-7 text-[11px]"
                              disabled={!!exporting} onClick={() => exportComparison('docx')}
                              data-testid="cognara-export-compare-docx">
                        {exporting === 'cmp-docx' ? <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                                                  : <Download className="w-3 h-3 mr-1" />}
                        Compare · Word
                      </Button>
                      <Button size="sm" variant="outline" className="h-7 text-[11px]"
                              disabled={!!exporting} onClick={() => exportComparison('pdf')}
                              data-testid="cognara-export-compare-pdf">
                        {exporting === 'cmp-pdf' ? <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                                                 : <Download className="w-3 h-3 mr-1" />}
                        Compare · PDF
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button size="sm" variant="outline" className="h-7 text-[11px]"
                              disabled={!!exporting || !hasAnalysis}
                              onClick={() => exportOnePager('pdf')}
                              data-testid="cognara-export-onepager">
                        {exporting === 'one-pdf' ? <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                                                 : <LayoutList className="w-3 h-3 mr-1" />}
                        One-pager
                      </Button>
                      {draft.kind === 'ld' && (
                        <>
                          <Button size="sm" variant="outline" className="h-7 text-[11px]"
                                  disabled={!!exporting || !hasAnalysis}
                                  onClick={() => exportAssessment('docx')}
                                  data-testid="cognara-export-assessment-docx">
                            {exporting === 'ld-docx' ? <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                                                     : <FileText className="w-3 h-3 mr-1" />}
                            Assessment · Word
                          </Button>
                          <Button size="sm" variant="outline" className="h-7 text-[11px]"
                                  disabled={!!exporting || !hasAnalysis}
                                  onClick={() => exportAssessment('pdf')}
                                  data-testid="cognara-export-assessment-pdf">
                            {exporting === 'ld-pdf' ? <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                                                    : <FileText className="w-3 h-3 mr-1" />}
                            Assessment · PDF
                          </Button>
                        </>
                      )}
                      <Button size="sm" variant="outline" className="h-7 text-[11px]"
                              disabled={!!exporting || !hasAnalysis}
                              onClick={() => exportReport('docx')}
                              data-testid="cognara-export-docx">
                        {exporting === 'docx' ? <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                                              : <Download className="w-3 h-3 mr-1" />}
                        Word
                      </Button>
                      <Button size="sm" className="h-7 text-[11px] bg-[#16324F] hover:bg-[#1F4E79]"
                              disabled={!!exporting || !hasAnalysis}
                              onClick={() => exportReport('pdf')}
                              data-testid="cognara-export-pdf">
                        {exporting === 'pdf' ? <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                                             : <Download className="w-3 h-3 mr-1" />}
                        PDF report
                      </Button>
                    </>
                  )}
                </div>
              </div>
              {draft.error && (
                <div className="rounded-lg border border-red-500/40 bg-red-500/5 p-2.5"
                     data-testid="cognara-session-error">
                  <p className="text-[11px] text-red-500 font-medium">
                    This session did not complete
                  </p>
                  <p className="text-[11px] text-slate-400 mt-0.5">{humanError(draft.error)}</p>
                </div>
              )}
              {!hasAnalysis && (
                <p className="text-xs text-slate-500 border border-dashed rounded-lg p-4 text-center"
                   data-testid="cognara-no-analysis">
                  There is no analysis on this session, so there is nothing to export or
                  question. Start a new session to run it again.
                </p>
              )}
              {activeRevision && (
                <p className="text-[11px] text-amber-500" data-testid="cognara-viewing-revision">
                  Viewing revision {activeRevision.revision}, superseded on your direction:
                  “{activeRevision.direction}”. Exports will carry this revision.
                </p>
              )}
              {hasAnalysis && (<>
              <div className="rounded-lg border p-3">
                <div className="flex items-start gap-2">
                  <Scale className="w-4 h-4 text-violet-500 mt-0.5 shrink-0" />
                  <div className="min-w-0">
                    <p className="text-sm font-medium" data-testid="cognara-headline">{a.headline}</p>
                    <div className="flex flex-wrap items-center gap-2 mt-1.5">
                      <Badge variant="outline" className="text-[10px]">
                        Strength {a.verdict?.strength ?? '—'}/10
                      </Badge>
                      <Badge variant="outline" className="text-[10px]">
                        Confidence {a.verdict?.confidence != null
                          ? `${Math.round(a.verdict.confidence * 100)}%` : '—'}
                      </Badge>
                      <Badge variant="outline"
                             className={`text-[10px] ${TIMEBAR_TONE[a.timeBar?.status] || ''}`}>
                        Time bar: {a.timeBar?.status || 'UNKNOWN'}
                      </Badge>
                      <Badge variant="secondary" className="text-[10px]">{draft.mode?.replace('_', ' ')}</Badge>
                    </div>
                    <p className="text-xs text-slate-400 mt-2">{a.verdict?.recommendation}</p>
                  </div>
                </div>
              </div>

              {a.verdict?.mustFixFirst?.length > 0 && (
                <Section icon={TriangleAlert} tone="text-amber-500" title="Fix before submitting"
                         testId="cognara-mustfix">
                  <ul className="list-disc pl-4 space-y-0.5">
                    {a.verdict.mustFixFirst.map((x, i) => <li key={i}>{x}</li>)}
                  </ul>
                </Section>
              )}

              <Section icon={Gavel} title="Entitlement" testId="cognara-entitlement">
                <div className="space-y-1.5">
                  {(a.entitlementBasis || []).map((e, i) => (
                    <div key={i} className="flex items-start gap-2">
                      <span className={`px-1.5 py-0.5 rounded border text-[9px] shrink-0 ${STRENGTH_TONE[e.strength] || ''}`}>
                        {e.strength}
                      </span>
                      <div>
                        {e.clauseRef && <span className="font-mono text-[10px] text-slate-400 mr-1">[{e.clauseRef}]</span>}
                        {e.summary}
                      </div>
                    </div>
                  ))}
                </div>
              </Section>

              {a.quantum && (
                <Section icon={Scale} title="Quantum" testId="cognara-quantum">
                  <div className="flex flex-wrap gap-x-5 gap-y-1 mb-2">
                    <Stat label="Claimed" value={money(a.quantum.claimedAmount, a.quantum.currency)} />
                    <Stat label="Days" value={a.quantum.claimedDays ?? '—'} />
                    <Stat label="Assessed range"
                          value={`${money(a.quantum.assessedLow, a.quantum.currency)} – ${money(a.quantum.assessedHigh, a.quantum.currency)}`} />
                    <Stat label="Most likely" value={money(a.quantum.assessedLikely, a.quantum.currency)} />
                  </div>
                  {a.quantum.method && <p className="text-[11px] text-slate-500 mb-1.5">{a.quantum.method}</p>}
                  {(a.quantum.buildUp || []).map((b, i) => (
                    <div key={i} className="flex justify-between gap-2 border-t border-border/40 py-0.5">
                      <span>{b.item} <span className="text-slate-500">· {b.basis}</span></span>
                      <span className="tabular-nums shrink-0">{money(b.amount, a.quantum.currency)}</span>
                    </div>
                  ))}
                </Section>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Section icon={CheckCircle2} tone="text-emerald-500" title="Our case"
                         testId="cognara-ourcase">
                  {(a.ourCase || []).map((p, i) => (
                    <div key={i} className="mb-1.5">
                      <p>{p.point}</p>
                      {p.support && <p className="text-[10px] text-slate-500">{p.support}</p>}
                    </div>
                  ))}
                </Section>
                <Section icon={ShieldAlert} tone="text-red-500" title="Likely response against us"
                         testId="cognara-counterparty">
                  {(a.counterpartyCase || []).map((p, i) => (
                    <div key={i} className="mb-1.5">
                      <p>{p.point}</p>
                      {p.support && <p className="text-[10px] text-slate-500">{p.support}</p>}
                      {p.howToRebut && (
                        <p className="text-[10px] text-sky-500 mt-0.5">Rebut: {p.howToRebut}</p>
                      )}
                    </div>
                  ))}
                </Section>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Section title="Evidence held" testId="cognara-evidence">
                  <ul className="list-disc pl-4 space-y-0.5">
                    {(a.evidenceAvailable || []).map((x, i) => <li key={i}>{x}</li>)}
                  </ul>
                </Section>
                <Section title="Evidence gaps" tone="text-amber-500" testId="cognara-gaps">
                  <ul className="list-disc pl-4 space-y-0.5">
                    {(a.evidenceGaps || []).map((x, i) => <li key={i}>{x}</li>)}
                  </ul>
                </Section>
              </div>

              {a.factMatrix?.length > 0 && (
                <Section title="Fact matrix" testId="cognara-facts">
                  <table className="w-full table-fixed">
                    <tbody>
                      {a.factMatrix.map((f, i) => (
                        <tr key={i} className="border-t border-border/40 align-top">
                          <td className="w-[86px] py-0.5 pr-2 tabular-nums align-top text-slate-400 break-words">
                            {f.date || '—'}
                          </td>
                          <td className="py-0.5 pr-2 break-words">{f.event}</td>
                          <td className="w-[32%] py-0.5 text-[10px] text-slate-500 align-top break-all">
                            {f.source}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </Section>
              )}

              {draft?.baseline?.currentValue > 0 && (
                <Section icon={Scale} title="The contract as it now stands"
                         testId="cognara-baseline">
                  <div className="grid grid-cols-2 gap-x-3 gap-y-1">
                    <p className="text-slate-500">Current value</p>
                    <p data-testid="baseline-current-value">
                      {Number(draft.baseline.currentValue).toLocaleString('en-IN')}{' '}
                      {draft.baseline.currency || 'INR'}
                      <span className="block text-[10px] text-slate-500">
                        {draft.baseline.currentValueSource}
                      </span>
                    </p>
                    <p className="text-slate-500">Current completion</p>
                    <p>{draft.baseline.currentCompletionDate || '—'}
                      <span className="block text-[10px] text-slate-500">
                        {draft.baseline.currentCompletionSource}
                      </span>
                    </p>
                    <p className="text-slate-500">Original value</p>
                    <p>{Number(draft.baseline.originalValue || 0).toLocaleString('en-IN')}
                      <span className="block text-[10px] text-slate-500">
                        {draft.baseline.originalRef}
                      </span>
                    </p>
                    <p className="text-slate-500">Liquidated damages</p>
                    <p>{[draft.baseline.ldClause, draft.baseline.ldRate, draft.baseline.ldCap]
                      .filter(Boolean).join(' · ') || '—'}</p>
                  </div>
                  {(draft.baseline.amendments || []).length > 0 && (
                    <div className="mt-1.5 border-t border-border/40 pt-1.5">
                      {draft.baseline.amendments.map((x, i) => (
                        <p key={i} className="text-[10px] text-slate-500"
                           data-testid={`baseline-amendment-${i}`}>
                          <span className="text-foreground">{x.ref} · {x.date}</span>{' '}
                          {x.revisedValue ? `${Number(x.revisedValue).toLocaleString('en-IN')} · ` : ''}
                          {x.summary} {x.source ? `(${x.source})` : ''}
                        </p>
                      ))}
                    </div>
                  )}
                  {draft.baseline.basis && (
                    <p className="mt-1.5 text-[10px] text-slate-500">{draft.baseline.basis}</p>
                  )}
                  {draft.baseline.confidence > 0 && (
                    <p className="mt-1 text-[10px] text-sky-500" data-testid="baseline-confidence">
                      Cognara's confidence in this baseline:{' '}
                      {Math.round(draft.baseline.confidence * 100)}%
                    </p>
                  )}
                </Section>
              )}

              {(() => {
                const issues = a.delayIssues || [];
                const owner = issues.filter(x => (x.side || '').toUpperCase() === 'OWNER');
                const vendor = issues.filter(x => (x.side || '').toUpperCase() === 'VENDOR');
                if (!owner.length && !vendor.length) return null;
                const List = ({ items, prefix }) => items.map((x, i) => (
                  <div key={i} className="mb-2" data-testid={`cognara-${prefix}-issue-${i}`}>
                    <p className="text-foreground">{x.issue}</p>
                    <p className={`text-[10px] ${x.impact === 'CRITICAL_PATH'
                      ? 'text-red-400' : 'text-slate-500'}`}>
                      {x.impact === 'CRITICAL_PATH' ? 'Critical path' : 'Colour only'}
                      {x.delayDays ? ` · ${x.delayDays} days` : ''}
                      {x.evidence ? ` · ${x.evidence}` : ''}
                      {x.confidence ? ` · ${Math.round(x.confidence * 100)}% confidence` : ''}
                    </p>
                    {x.consequence && <p className="text-slate-400">{x.consequence}</p>}
                    {x.quote && (
                      <p className="italic text-[10px] text-slate-500 border-l-2 border-border/60 pl-2 my-0.5">
                        "{x.quote}"
                      </p>
                    )}
                    {x.notShown && (
                      <p className="text-[10px] text-amber-500">
                        Not shown by the record: {x.notShown}
                      </p>
                    )}
                    {x.evidenceNeeded && (
                      <p className="text-[10px] text-sky-500">
                        Would be settled by: {x.evidenceNeeded}
                      </p>
                    )}
                  </div>
                ));
                return (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <Section icon={Building2} tone="text-sky-500"
                             title="Owner-side issues & consequences"
                             testId="cognara-owner-issues">
                      <List items={owner} prefix="owner" />
                    </Section>
                    <Section icon={HardHat} tone="text-orange-500"
                             title="Vendor-side issues & consequences"
                             testId="cognara-vendor-issues">
                      <List items={vendor} prefix="vendor" />
                    </Section>
                  </div>
                );
              })()}

              {a.evidenceToRequest?.length > 0 && (
                <Section icon={FileSignature} tone="text-sky-500"
                         title="Documentary evidence to call for"
                         testId="cognara-evidence-to-request">
                  {a.evidenceToRequest.map((x, i) => (
                    <div key={i} className="mb-1.5" data-testid={`cognara-ask-${i}`}>
                      <p className="text-foreground">{x.document}</p>
                      <p className="text-[10px] text-slate-500">
                        {x.fromParty ? `From ${x.fromParty} · ` : ''}{x.wouldSettle}
                      </p>
                    </div>
                  ))}
                </Section>
              )}

              {(a.keyReasons?.length > 0 || a.causationNarrative) && (
                <Section icon={Scale} title={kind === 'ld'
                  ? 'Key reasons leading to the LD assessment'
                  : 'Key reasons behind this assessment'}
                         testId="cognara-causation">
                  {(a.keyReasons || []).map((r, i) => (
                    <div key={i} className="mb-1.5" data-testid={`cognara-key-reason-${i}`}>
                      <p>
                        <span className={`mr-1.5 text-[10px] uppercase tracking-wider ${
                          r.attributedTo === 'OWNER' ? 'text-sky-500'
                          : r.attributedTo === 'VENDOR' ? 'text-orange-500'
                          : r.attributedTo === 'SHARED' ? 'text-amber-500' : 'text-slate-500'}`}>
                          {r.attributedTo}
                        </span>
                        {r.reason}
                      </p>
                      <p className="text-[10px] text-slate-500">
                        {r.effect}
                        {r.confidence ? ` · ${Math.round(r.confidence * 100)}% confidence` : ''}
                      </p>
                    </div>
                  ))}
                  {a.causationNarrative && (
                    <div className="mt-1.5 space-y-1.5 border-t border-border/40 pt-1.5">
                      {String(a.causationNarrative).split('\n').filter(p => p.trim()).map((p, i) => (
                        <p key={i} className="text-slate-400">{p.trim()}</p>
                      ))}
                    </div>
                  )}
                </Section>
              )}

              {a.citations?.length > 0 && (
                <Section icon={Quote} title="Cited from your documents" testId="cognara-citations">
                  <p className="text-[11px] text-slate-500 mb-1.5">
                    Tap a citation to open the page or row it came from.
                  </p>
                  {a.citations.map((c, i) => {
                    const spot = citationSpot(c.locator);
                    const body = (
                      <>
                        <p className="italic">"{c.quote}"</p>
                        <p className="text-[10px] text-slate-500">
                          {c.document} {c.locator ? `· ${c.locator}` : ''}
                          {spot && (
                            <span className="text-orange-400 ml-1">
                              · open {spot.row ? `row ${spot.row}` : `p. ${spot.page}`}
                            </span>
                          )}
                        </p>
                      </>
                    );
                    return spot ? (
                      <button key={i} onClick={() => openSource(c)}
                              className="w-full text-left mb-1.5 border-l-2 border-violet-500/40
                                         pl-2 rounded-r hover:bg-violet-500/10 transition-colors"
                              data-testid={`cognara-citation-${i}`}>
                        {body}
                      </button>
                    ) : (
                      <div key={i} className="mb-1.5 border-l-2 border-violet-500/40 pl-2"
                           data-testid={`cognara-citation-${i}`}>
                        {body}
                      </div>
                    );
                  })}
                </Section>
              )}
              {draft?.vision_pages?.length > 0 && (
                <Section icon={Quote} title="Pages read from the page image"
                         testId="cognara-vision-pages">
                  <p className="text-[11px] text-slate-500 mb-1.5">
                    OCR was unreliable on these pages (stamps, signatures, faded print or
                    ruled tables), so Cognara opened them as images and read them directly.
                    Figures quoted from them come from the page images, not the OCR text.
                  </p>
                  {draft.vision_pages.map((v, i) => (
                    <div key={i} className="mb-1 border-l-2 border-emerald-500/40 pl-2"
                         data-testid={`cognara-vision-page-${i}`}>
                      <p className="text-foreground">{v.document}</p>
                      <p className="text-[10px] text-slate-500">
                        page {(v.pages || []).join(', ')} · {(v.reasons || []).join('; ')}
                      </p>
                    </div>
                  ))}
                </Section>
              )}
              {draft?.coverage?.length > 0 && (
                <Section icon={Quote} title="Read in full, end to end"
                         testId="cognara-coverage">
                  <p className="text-[11px] text-slate-500 mb-1.5">
                    Every page, row and table of every selected document was read in
                    sequence and indexed before the assessment was written.
                  </p>
                  {draft.coverage.map((c, i) => (
                    <div key={i} className="mb-1 border-l-2 border-sky-500/40 pl-2"
                         data-testid={`cognara-coverage-${i}`}>
                      <p className="text-foreground break-words">{c.document}</p>
                      <p className="text-[10px] text-slate-500">
                        {c.pages_swept || c.pages_total} of {c.pages_total}{' '}
                        {c.unit === 'section' ? 'section' : 'page'}(s) read
                      </p>
                    </div>
                  ))}
                </Section>
              )}
              </>)}

              {/* The operative letter that follows from the analysis. */}
              {hasAnalysis && !activeRevision && (
                <Section icon={FileSignature} tone="text-amber-500"
                         title="Issue or reject, in writing" testId="cognara-letters">
                  <p className="text-[11px] text-slate-500 mb-2">
                    Draft the letter this analysis supports. It is written from the analysis
                    and the review thread, and exports in the same format.
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {(kind === 'ld'
                      ? [['IMPOSE_LD', 'Impose LD'], ['REJECT_LD', 'Reject LD imposition']]
                      : [['ADMIT_CLAIM', 'Admit the claim'], ['REJECT_CLAIM', 'Reject the claim']]
                    ).map(([key, text]) => (
                      <Button key={key} size="sm" variant="outline" className="h-7 text-[11px]"
                              disabled={!!lettering} onClick={() => draftLetter(key)}
                              data-testid={`cognara-letter-${key.toLowerCase()}`}>
                        {lettering === key ? <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                                           : <FileSignature className="w-3 h-3 mr-1" />}
                        {text}
                      </Button>
                    ))}
                  </div>
                  <div className="space-y-2 mt-2">
                    {(draft.letters || letters).map((l) => (
                      <div key={l.letter_id} className="rounded border p-2"
                           data-testid={`cognara-letter-row-${l.letter_id}`}>
                        <p className="text-[11px] font-medium">{l.decisionTitle}</p>
                        {l.status === 'RUNNING' ? (
                          <p className="text-[11px] text-slate-500 flex items-center gap-1">
                            <Loader2 className="w-3 h-3 animate-spin" /> Drafting…
                          </p>
                        ) : l.status === 'FAILED' ? (
                          <p className="text-[11px] text-red-500">{l.error}</p>
                        ) : (
                          <>
                        <p className="text-[11px] text-slate-400">{l.subject}</p>
                        <p className="text-[10px] text-slate-500 mt-0.5 line-clamp-3">
                          {l.openingStatement}
                        </p>
                        <div className="flex gap-1.5 mt-1.5">
                          <Button size="sm" variant="outline" className="h-6 text-[10px]"
                                  disabled={!!exporting}
                                  onClick={() => exportLetter(l.letter_id, 'docx')}
                                  data-testid={`cognara-letter-docx-${l.letter_id}`}>
                            <Download className="w-3 h-3 mr-1" /> Word
                          </Button>
                          <Button size="sm" variant="outline" className="h-6 text-[10px]"
                                  disabled={!!exporting}
                                  onClick={() => exportLetter(l.letter_id, 'pdf')}
                                  data-testid={`cognara-letter-pdf-${l.letter_id}`}>
                            <Download className="w-3 h-3 mr-1" /> PDF
                          </Button>
                        </div>
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                </Section>
              )}

              {/* Question the analysis, and have it rebuilt when the answer is
                  not good enough — nothing is exported until it reads right. */}
              {hasAnalysis && (
              <Section icon={MessageSquare} tone="text-sky-500"
                       title="Question this analysis" testId="cognara-review-thread">
                {thread.length === 0 && (
                  <p className="text-[11px] text-slate-500 mb-2">
                    Push back on anything here — the answer is drawn from the same
                    documents and travels into the exported report.
                  </p>
                )}

                {/* The exchange reads as a conversation: your question on the right,
                    the assessment's answer on the left. */}
                <div className="space-y-2.5 mb-2 max-h-[46vh] overflow-y-auto pr-1"
                     data-testid="cognara-thread">
                  {thread.map((t, i) => (
                    <div key={i} className="space-y-1.5">
                      <div className="flex justify-end">
                        <p className="max-w-[88%] rounded-2xl rounded-br-sm bg-sky-500/15
                                      border border-sky-500/25 px-2.5 py-1.5 text-[11px]
                                      text-sky-100 whitespace-pre-wrap break-words"
                           data-testid={`cognara-question-${i}`}>
                          {t.question}
                        </p>
                      </div>
                      <div className="flex justify-start">
                        <div className="max-w-[92%] rounded-2xl rounded-bl-sm bg-muted/40
                                        border border-border/60 px-2.5 py-1.5">
                          {t.answer ? (
                            <div className="text-[11px] text-slate-300 break-words
                                            [&_p]:mb-1.5 [&_strong]:text-slate-100
                                            [&_ul]:list-disc [&_ul]:pl-4 [&_li]:mb-0.5"
                                 data-testid={`cognara-answer-${i}`}>
                              <ReactMarkdown>{t.answer}</ReactMarkdown>
                            </div>
                          ) : t.status === 'FAILED' ? (
                            <p className="text-[11px] text-rose-400"
                               data-testid={`cognara-answer-failed-${i}`}>
                              {t.error || 'That question could not be answered'}
                            </p>
                          ) : (
                            <p className="text-[11px] text-slate-500 flex items-center gap-1.5"
                               data-testid={`cognara-answer-pending-${i}`}>
                              <Loader2 className="w-3 h-3 animate-spin" />
                              Working through the analysis and the pages behind it…
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {!activeRevision && (
                  <>
                    {thread.length === 0 && (
                      <div className="flex flex-wrap gap-1.5 mb-2"
                           data-testid="cognara-suggested-questions">
                        {['What is the weakest part of this position?',
                          'Which figure here is least supported by the documents?',
                          'What would the other side say first?'].map((q) => (
                          <button key={q} type="button" disabled={asking || revising}
                                  onClick={() => setQuestion(q)}
                                  data-testid={`cognara-suggest-${q.slice(0, 12)}`}
                                  className="px-2 py-1 rounded-full border border-border/60
                                             text-[10px] text-slate-400 hover:text-slate-100
                                             hover:border-sky-500/50 transition-colors">
                            {q}
                          </button>
                        ))}
                      </div>
                    )}

                    <div className="flex items-end gap-1.5">
                      <Textarea value={question} onChange={e => setQuestion(e.target.value)}
                                onKeyDown={e => {
                                  if (e.key === 'Enter' && !e.shiftKey) {
                                    e.preventDefault();
                                    if (!asking && question.trim()) ask();
                                  }
                                }}
                                disabled={asking || revising} rows={2}
                                placeholder="Ask anything about this assessment — Enter to send, Shift+Enter for a new line"
                                className="text-xs resize-none min-h-[52px]"
                                data-testid="cognara-question-input" />
                      <Button size="sm" className="h-[52px] px-3" onClick={ask}
                              disabled={asking || revising || !question.trim()}
                              data-testid="cognara-ask">
                        {asking ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                : <Send className="w-3.5 h-3.5" />}
                      </Button>
                    </div>

                    <div className="mt-2.5 border-t border-border/50 pt-2.5">
                      <p className="text-[10px] uppercase tracking-widest text-slate-500 mb-1.5">
                        Or rebuild it
                      </p>
                      <div className="flex gap-1.5">
                        <Input value={direction} onChange={e => setDirection(e.target.value)}
                               disabled={asking || revising}
                               placeholder="Say what to change, e.g. assess LD on the amended price only"
                               className="h-8 text-xs" data-testid="cognara-direction-input" />
                        <Button size="sm" variant="outline" className="h-8 text-[11px]"
                                onClick={revise} disabled={asking || revising || !direction.trim()}
                                data-testid="cognara-revise">
                          {revising ? <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                                    : <RefreshCw className="w-3 h-3 mr-1" />}
                          Revise
                        </Button>
                      </div>
                      <p className="text-[10px] text-slate-500 mt-1">
                        A revision rebuilds the whole analysis as revision
                        {' '}{(draft.revision || 1) + 1}. The current one is kept and stays
                        exportable.
                      </p>
                    </div>
                  </>
                )}
              </Section>
              )}
            </div>

            {/* Editable draft */}
            <div className="lg:col-span-2 overflow-y-auto border-l pl-4 space-y-3">
              {hasAnalysis && (<>
              <div>
                <p className="text-sm font-medium">Drafted {kind === 'ld' ? 'LD tracker' : KIND_LABEL[kind]?.toLowerCase()}</p>
                <p className="text-[10px] text-slate-500">
                  Edit anything before accepting — what you see is exactly what gets created.
                </p>
              </div>
              {(FIELDS[kind] || []).map(([k, label, type]) => field(k, label, type))}
              </>)}
              {hasAnalysis && draft.status === 'PENDING' && (
              <div className="flex gap-2 pt-2 sticky bottom-0 bg-background pb-1">
                <Button variant="outline" size="sm" onClick={reject} disabled={accepting}
                        data-testid="cognara-reject">
                  <XCircle className="w-3 h-3 mr-1" /> Reject
                </Button>
                <Button size="sm" className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                        onClick={accept} disabled={accepting} data-testid="cognara-accept">
                  {accepting ? <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                             : <CheckCircle2 className="w-3 h-3 mr-1" />}
                  Accept & create
                </Button>
              </div>
              )}
            </div>
          </div>
        )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
    <SourceViewer source={source} onClose={() => setSource(null)} />
    </>
  );
};

/** The paper behind a citation: the cited page, or the cited rows in context. */
const SourceViewer = ({ source, onClose }) => (
  <Dialog open={!!source} onOpenChange={o => { if (!o) onClose(); }}>
    <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto"
                   data-testid="cognara-source-viewer">
      <DialogHeader>
        <DialogTitle className="text-sm">
          {source?.citation?.document}
          {source?.spot?.row ? ` · row ${source.spot.row}` : ''}
          {source?.spot?.page ? ` · page ${source.spot.page}` : ''}
        </DialogTitle>
        <DialogDescription className="text-[11px]">
          {source?.citation?.locator
            ? `Cited as ${source.citation.locator}`
            : 'The source behind this citation'}
        </DialogDescription>
      </DialogHeader>
      {source?.loading && (
        <div className="flex items-center gap-2 text-xs text-slate-500 py-8 justify-center"
             data-testid="cognara-source-loading">
          <Loader2 className="w-4 h-4 animate-spin" /> Opening the source…
        </div>
      )}
      {source?.error && (
        <p className="text-xs text-red-400 py-6" data-testid="cognara-source-error">
          {source.error}
        </p>
      )}
      {source?.data?.kind === 'page' && (
        <div className="space-y-2">
          <img src={source.data.image} alt={`Page ${source.data.page}`}
               className="w-full rounded border border-border/60"
               data-testid="cognara-source-page-image" />
          {source.data.text && (
            <details className="text-[11px] text-slate-400">
              <summary className="cursor-pointer">Transcription of this page</summary>
              <pre className="whitespace-pre-wrap mt-1 text-[10px] leading-relaxed">
                {source.data.text}
              </pre>
            </details>
          )}
        </div>
      )}
      {source?.data?.kind === 'rows' && (
        <div className="text-[11px]" data-testid="cognara-source-rows">
          {source.data.sheet && (
            <p className="text-slate-500 mb-1.5">Sheet “{source.data.sheet}”</p>
          )}
          <div className="rounded border border-border/60 divide-y divide-border/40">
            {source.data.rows.map((r, i) => (
              <div key={i}
                   className={`flex gap-2 px-2 py-1 ${r.target
                     ? 'bg-orange-500/15 text-foreground' : 'text-slate-400'}`}
                   data-testid={r.target ? 'cognara-source-row-target' : undefined}>
                <span className="w-10 shrink-0 text-slate-500">{r.n ? `R${r.n}` : ''}</span>
                <span className="break-words">{r.text}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </DialogContent>
  </Dialog>
);

const SESSION_TONE = {
  PENDING: 'text-emerald-500', RUNNING: 'text-sky-500',
  ACCEPTED: 'text-violet-500', REJECTED: 'text-slate-500', FAILED: 'text-red-500',
};

const SessionRail = ({ sessions, activeId, onOpen, onNew }) => (
  <div className="w-52 shrink-0 border-r pr-4 flex flex-col" data-testid="cognara-session-rail">
    <Button size="sm" variant="outline" className="h-8 text-[11px] mb-2"
            onClick={onNew} data-testid="cognara-new-session">
      <Plus className="w-3 h-3 mr-1" /> New session
    </Button>
    <div className="flex-1 min-h-0 overflow-y-auto">
      <div className="space-y-1 pr-1">
        {sessions.length === 0 && (
          <p className="text-[10px] text-slate-500 px-1">
            No sessions yet. Each run is saved here, so you can keep several readings of the
            same papers side by side.
          </p>
        )}
        {sessions.map(s => (
          <button key={s.id} onClick={() => onOpen(s.id)}
                  className={`w-full text-left rounded-md px-2 py-1.5 border transition-colors
                    ${s.id === activeId ? 'border-orange-500/60 bg-orange-500/10'
                                        : 'border-transparent hover:bg-muted/60'}`}
                  data-testid={`cognara-session-${s.id}`}>
            <p className="text-[11px] font-medium truncate">
              {s.label || `${s.kind?.toUpperCase()} · ${s.mode?.replace(/_/g, ' ')}`}
            </p>
            {s.analysis?.headline && (
              <p className="text-[9px] text-slate-500"
                 style={{ display: '-webkit-box', WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical', overflow: 'hidden',
                          overflowWrap: 'anywhere' }}>
                {s.analysis.headline}
              </p>
            )}
            <p className="text-[9px] text-slate-500 flex flex-wrap items-center gap-1">
              <Clock className="w-2.5 h-2.5" />
              {s.created_at ? new Date(s.created_at).toLocaleString('en-GB',
                { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : ''}
              <span className={SESSION_TONE[s.status] || 'text-slate-500'}>· {s.status}</span>
              {(s.revision || 1) > 1 && <span>· rev {s.revision}</span>}
            </p>
          </button>
        ))}
      </div>
    </div>
  </div>
);

const Section = ({ icon: Icon, title, children, tone = 'text-slate-400', testId }) => (
  <div className="rounded-lg border p-3 text-xs" data-testid={testId}>
    <p className={`text-[10px] uppercase tracking-wider mb-1.5 flex items-center gap-1.5 ${tone}`}>
      {Icon && <Icon className="w-3 h-3" />} {title}
    </p>
    {children}
  </div>
);

const Stat = ({ label, value }) => (
  <div>
    <p className="text-[9px] uppercase tracking-wider text-slate-500">{label}</p>
    <p className="tabular-nums">{value}</p>
  </div>
);

export default CognaraDraftStudio;
